import React, { useMemo, useRef, useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { AddressEditor, AddressData, EmailEditor, EmailData } from "../shared";
import { PhoneInput } from "@/components/ui/PhoneInput";
import { TextInput } from "@/components/ui/TextInput";
import useToast from "@/hooks/useToast";
import { theme } from "@/constants/themes";
import { ClientStatusType } from "@/types/db/clients";
import { Button } from "@/components/ui/Button";
import { MarketsInput } from "@/components/inputs/MarketsInput";
import { ContactsInput } from "@/components/inputs/ContactsInput";
import { supabase } from "@/lib/supabase-client";

const SAGE_ID_REGEX = /^C-\d{4}$/;

export { ClientStatusType };

// Status options for dropdown
const STATUS_OPTIONS = [
  { text: "ACTIVE", color: "complete" },
  { text: "INACTIVE", color: "neutral" },
] as const;

export function getClientStatusColor(status: string, colors: theme): string {
  const mapping: Record<string, string> = {
    ACTIVE: colors.complete.border,
    INACTIVE: colors.neutral.border,
  };
  return mapping[status.toUpperCase()] || colors.neutral.border;
}

export const CLIENT_STATUS_OPTIONS = STATUS_OPTIONS;

export type ClientForm = {
  name: string;
  description?: string;
  status: ClientStatusType;
  meta_data?: {
    address?: AddressData;
    emails?: EmailData[];
    phone?: string;
    industry?: string;
    markets?: string[];
    sage_id?: string;
  };
  client_contacts?: {
    contact: {
      id: string;
      first_name: string;
      last_name: string;
      preferred_name?: string;
    };
    note?: string;
  }[];
};

export type ClientPayload = { client: ClientForm };

const initialForm: ClientForm = {
  name: "",
  description: "",
  status: "ACTIVE",
  meta_data: {
    address: {
      street: "",
      city: "",
      state: "",
      zip: "",
    },
    emails: [],
    phone: "",
    industry: "",
    markets: [],
  },
  client_contacts: [],
};

type ClientCreateFormProps = {
  onSave?: (payload: ClientPayload) => Promise<void> | void;
  onChange?: (payload: ClientPayload) => void;
  onDirtyChange?: (dirty: boolean) => void;
  initialClient?: Partial<ClientForm>;
};

export interface ClientCreateFormRef {
  handleSave: () => Promise<void>;
  handleReset: () => void;
  updateName: (name: string) => void;
  updateDescription: (description: string) => void;
  updateStatus: (status: string) => void;
}

export const ClientCreateForm = forwardRef<ClientCreateFormRef, ClientCreateFormProps>(
  ({ onSave, onChange, onDirtyChange, initialClient }, ref) => {
    const { colors } = useTheme();
    const toast = useToast();

    const [form, setForm] = useState<ClientForm>({
      ...initialForm,
      ...initialClient,
    });

    // Sage ID validation
    const [sageIdError, setSageIdError] = useState<string | undefined>();
    useEffect(() => {
      const sageId = form.meta_data?.sage_id?.trim();

      if (!sageId) {
        setSageIdError(undefined);
        return;
      }

      if (!SAGE_ID_REGEX.test(sageId)) {
        setSageIdError("Format must be C-0000");
        return;
      }

      const timeoutId = setTimeout(async () => {
        try {
          const { data, error } = await supabase
            .from("clients")
            .select("id")
            .eq("meta_data->>sage_id", sageId)
            .limit(1);

          if (error) {
            console.error("Error checking Sage ID:", error);
            setSageIdError(undefined);
          } else if (data && data.length > 0) {
            setSageIdError("Sage ID already exists");
          } else {
            setSageIdError(undefined);
          }
        } catch (err) {
          console.error("Error checking Sage ID:", err);
          setSageIdError(undefined);
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    }, [form.meta_data?.sage_id]);

    const baseline = useRef(JSON.stringify({ client: form }));
    const dirty = useMemo(() => {
      const isDirty = JSON.stringify({ client: form }) !== baseline.current;
      onDirtyChange?.(isDirty);
      return isDirty;
    }, [form, onDirtyChange]);

    // Expose methods to parent
    useImperativeHandle(ref, () => ({
      handleSave,
      handleReset: () => {
        const nextForm: ClientForm = {
          ...initialForm,
          ...initialClient,
        };
        setForm(nextForm);
        baseline.current = JSON.stringify({ client: nextForm });
      },
      updateName: (name: string) => {
        updateForm("name", name);
      },
      updateDescription: (description: string) => {
        updateForm("description", description);
      },
      updateStatus: (status: string) => {
        updateForm("status", status as ClientStatusType);
      },
    }));

    const updateForm = <K extends keyof ClientForm>(key: K, value: ClientForm[K]) => {
      const newForm = { ...form, [key]: value };
      setForm(newForm);
      onChange?.({ client: newForm });
    };

    const updateAddress = (address: AddressData) => {
      const newForm = {
        ...form,
        meta_data: {
          ...form.meta_data,
          address,
        },
      };
      setForm(newForm);
      onChange?.({ client: newForm });
    };

    const updateEmails = (emails: EmailData[]) => {
      const newForm = {
        ...form,
        meta_data: {
          ...form.meta_data,
          emails,
        },
      };
      setForm(newForm);
      onChange?.({ client: newForm });
    };

    const updatePhone = (phone: string) => {
      const newForm = {
        ...form,
        meta_data: {
          ...form.meta_data,
          phone,
        },
      };
      setForm(newForm);
      onChange?.({ client: newForm });
    };

    const updateIndustry = (industry: string) => {
      const newForm = {
        ...form,
        meta_data: {
          ...form.meta_data,
          industry,
        },
      };
      setForm(newForm);
      onChange?.({ client: newForm });
    };

    const handleAddEmail = () => {
      const currentEmails = form.meta_data?.emails || [];
      if (currentEmails.length >= 5) {
        toast.showToast({
          title: "Maximum emails reached",
          message: "You can add up to 5 email addresses",
          toastType: "warning",
        });
        return;
      }
      updateEmails([...currentEmails, { name: "", address: "" }]);
    };

    const handleSave = async () => {
      // Basic validation
      if (!form.name?.trim()) {
        toast.showToast({
          title: "Missing Client Name",
          message: "Please enter a name for this client",
          toastType: "warning",
        });
        return;
      }

      try {
        await onSave?.({ client: form });
      } catch (error) {
        // Re-throw to parent for proper error handling
        throw error;
      }
    };

    return (
      <View style={styles.container}>
        <ScrollView contentContainerStyle={{ gap: 8 }}>
          {/* Emails */}
          <View style={[styles.card, { borderColor: colors.border }]}>
            <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
              <ThemedText type="defaultSemiBold">Emails</ThemedText>
              <Button
                testID="client-add-email-button"
                text="Add Email"
                icon="plus"
                style={{
                  height: 32,
                  paddingHorizontal: 12,
                  backgroundColor: colors.sky.border,
                }}
                onPress={handleAddEmail}
                disabled={(form.meta_data?.emails?.length || 0) >= 5}
              />
            </View>
            {form.meta_data?.emails && form.meta_data.emails.length > 0 ? (
              <EmailEditor emails={form.meta_data.emails} onChange={updateEmails} editing />
            ) : (
              <ThemedText style={{ opacity: 0.5 }}>No emails added</ThemedText>
            )}
          </View>

          {/* Phone */}
          <View style={[styles.card, { borderColor: colors.border }]}>
            <ThemedText type="defaultSemiBold">Phone</ThemedText>
            <PhoneInput value={form.meta_data?.phone || ""} onChangeText={updatePhone} editable />
          </View>

          {/* Industry */}
          <View style={[styles.card, { borderColor: colors.border }]}>
            <ThemedText type="defaultSemiBold">Industry</ThemedText>
            <TextInput
              value={form.meta_data?.industry || ""}
              onChangeText={updateIndustry}
              placeholder="e.g. Technology, Healthcare, Finance"
              editable
            />
          </View>

          {/* Sage ID */}
          <View style={[styles.card, { borderColor: colors.border }]}>
            <ThemedText type="defaultSemiBold">Sage ID</ThemedText>
            <TextInput
              value={form.meta_data?.sage_id || ""}
              onChangeText={(text) => {
                const newForm = {
                  ...form,
                  meta_data: { ...form.meta_data, sage_id: text },
                };
                setForm(newForm);
                onChange?.({ client: newForm });
              }}
              placeholder="C-0000"
              error={sageIdError}
              editable
            />
          </View>

          {/* Address */}
          <View style={[styles.card, { borderColor: colors.border }]}>
            <ThemedText type="defaultSemiBold">Address</ThemedText>
            <AddressEditor address={form.meta_data?.address} onChange={updateAddress} editing />
          </View>

          {/* Markets */}
          <MarketsInput
            value={form.meta_data?.markets || []}
            state="editing"
            onChange={(ids) => {
              const newForm = {
                ...form,
                meta_data: { ...form.meta_data, markets: ids },
              };
              setForm(newForm);
              onChange?.({ client: newForm });
            }}
          />

          {/* Contacts */}
          <ContactsInput
            value={(form.client_contacts || []).map((cc) => cc.contact)}
            state="editing"
            onChange={(contacts) => {
              updateForm(
                "client_contacts",
                contacts.map((c) => ({
                  contact: {
                    id: c.id,
                    first_name: c.first_name ?? "",
                    last_name: c.last_name ?? "",
                    preferred_name: c.preferred_name,
                  },
                })),
              );
            }}
          />
        </ScrollView>
      </View>
    );
  },
);

ClientCreateForm.displayName = "ClientCreateForm";

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  card: {
    gap: 8,
    padding: 12,
    borderRadius: 10,
    borderWidth: StyleSheet.hairlineWidth,
  },
});
